import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronsUpDown, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ServiceRatioInput } from "@/graphql/__generated__/graphql";

interface ServiceOption {
  id: number;
  name: string;
  operationalOrganizationShortName: string;
}

interface ServiceRatioSelectProps {
  services: ServiceOption[];
  selectedServices: ServiceRatioInput[];
  onServicesChange: (_services: ServiceRatioInput[]) => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * ServiceRatioSelect component for selecting services and distributing ratios.
 * Allows adding services with automatic even distribution of ratios (total = 100).
 *
 * @param {ServiceRatioSelectProps} props - Component props
 * @returns {JSX.Element} The ServiceRatioSelect component
 */
const ServiceRatioSelect = ({
  services,
  selectedServices,
  onServicesChange,
  disabled = false,
  loading = false,
}: ServiceRatioSelectProps): JSX.Element => {
  const { t } = useTranslation(["translation", "common"]);
  const [open, setOpen] = useState(false);

  // Get available services (not already selected)
  const availableServices = services.filter(
    (service) => !selectedServices.some((s) => s.serviceId === service.id)
  );

  // Calculate total ratio
  const totalRatio = selectedServices.reduce((sum, s) => sum + s.ratio, 0);

  /**
   * Add a new service and redistribute ratios evenly
   */
  const handleAddService = (serviceId: number) => {
    const newServices = [...selectedServices, { serviceId, ratio: 0 }];
    // Distribute ratios evenly
    const evenRatio = Math.floor(100 / newServices.length);
    const remainder = 100 - evenRatio * newServices.length;

    const redistributed = newServices.map((s, index) => ({
      ...s,
      ratio: evenRatio + (index < remainder ? 1 : 0),
    }));

    onServicesChange(redistributed);
    setOpen(false);
  };

  /**
   * Remove a service and redistribute ratios evenly
   */
  const handleRemoveService = (serviceId: number) => {
    const newServices = selectedServices.filter((s) => s.serviceId !== serviceId);

    if (newServices.length === 0) {
      onServicesChange([]);
      return;
    }

    // Redistribute ratios evenly
    const evenRatio = Math.floor(100 / newServices.length);
    const remainder = 100 - evenRatio * newServices.length;

    const redistributed = newServices.map((s, index) => ({
      ...s,
      ratio: evenRatio + (index < remainder ? 1 : 0),
    }));

    onServicesChange(redistributed);
  };

  /**
   * Update ratio for a specific service
   */
  const handleRatioChange = (serviceId: number, newRatio: number) => {
    // Clamp the ratio between 0 and 100
    const clampedRatio = Math.max(0, Math.min(100, newRatio));

    const updatedServices = selectedServices.map((s) =>
      s.serviceId === serviceId ? { ...s, ratio: clampedRatio } : s
    );

    onServicesChange(updatedServices);
  };

  /**
   * Redistribute ratios evenly across all selected services
   */
  const handleRedistributeEvenly = () => {
    if (selectedServices.length === 0) return;

    const evenRatio = Math.floor(100 / selectedServices.length);
    const remainder = 100 - evenRatio * selectedServices.length;

    const redistributed = selectedServices.map((s, index) => ({
      ...s,
      ratio: evenRatio + (index < remainder ? 1 : 0),
    }));

    onServicesChange(redistributed);
  };

  // Get service name by ID
  const getServiceName = (serviceId: number) => {
    const service = services.find((s) => s.id === serviceId);
    return service ? `${service.name} (${service.operationalOrganizationShortName})` : `Service ${serviceId}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{t("serviceratio.title")} <span className="text-muted-foreground">({t("common:optional")})</span></Label>
        {selectedServices.length > 1 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRedistributeEvenly}
            disabled={disabled}
          >
            {t("serviceratio.redistributeevenly")}
          </Button>
        )}
      </div>

      {/* Selected services with ratio inputs */}
      {selectedServices.length > 0 && (
        <div className="space-y-2">
          {selectedServices.map((selectedService) => (
            <div
              key={selectedService.serviceId}
              className="flex items-center gap-2 rounded-md border p-2"
            >
              <div className="flex-1 truncate text-sm">
                {getServiceName(selectedService.serviceId)}
              </div>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={selectedService.ratio}
                  onChange={(e) =>
                    handleRatioChange(
                      selectedService.serviceId,
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="w-16 text-center"
                  disabled={disabled}
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => handleRemoveService(selectedService.serviceId)}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}

          {/* Total indicator */}
          <div className={cn(
            "flex items-center justify-end gap-2 text-sm",
            totalRatio !== 100 ? "text-destructive" : "text-muted-foreground"
          )}>
            <span>{t("serviceratio.total")}:</span>
            <span className="font-medium">{totalRatio}%</span>
            {totalRatio !== 100 && (
              <span className="text-xs">({t("serviceratio.mustbe100")})</span>
            )}
          </div>
        </div>
      )}

      {/* Add service dropdown */}
      {availableServices.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={disabled || loading}
            >
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                {t("serviceratio.addservice")}
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            onWheel={(e) => e.stopPropagation()}
          >
            <Command>
              <CommandInput placeholder={t("serviceratio.searchservice")} />
              <CommandList>
                <CommandEmpty>{t("common:forms.noresults")}</CommandEmpty>
                <CommandGroup>
                  {availableServices.map((service) => (
                    <CommandItem
                      key={service.id}
                      value={`${service.name} ${service.operationalOrganizationShortName}`}
                      onSelect={() => handleAddService(service.id)}
                    >
                      <div className="flex flex-col">
                        <span>{service.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {service.operationalOrganizationShortName}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {/* Empty state */}
      {services.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground">
          {t("serviceratio.noservicesavailable")}
        </p>
      )}
    </div>
  );
};

export default ServiceRatioSelect;
